import { createStyles, Group } from '@mantine/core';
import { space } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { AiRightSideCanvasObject } from '../../features/AiReferencedObjectCard';
import HorizontalResizeHandle from '../HorizontalResizeHandle';
import type { RightSideCanvasStore } from './store';

const useRightSideCanvasStyles = createStyles(
	(
		theme,
		{
			isDragging,
			store,
		}: {
			isDragging: boolean;
			store: RightSideCanvasStore<AiRightSideCanvasObject>;
		}
	) => {
		let width =
			store.pageSideCanvasMode.mode === 'hide' ? 0 : store.pageSideCanvasWidth;

		return {
			wrapper: {
				position: 'relative',
				height: '100%',
				overflow: 'hidden',
				scrollbarColor: `${theme.other.getColor(
					'fill/tertiary/default'
				)} transparent`,
				width: `${width}px`,
				...(isDragging
					? {}
					: {
							transitionProperty: 'width',
							transitionTimingFunction:
								store.pageSideCanvasMode.mode === 'show'
									? 'ease-out'
									: 'ease-in',
							transitionDuration:
								store.pageSideCanvasMode.mode === 'show' ? '300ms' : '200ms',
						}),
			},
		};
	}
);

export const RightSideCanvasWrapper = observer(
	({
		children,
		classNames,
		store,
	}: {
		children: ReactNode;
		classNames?: Record<string, string>;
		store: RightSideCanvasStore<AiRightSideCanvasObject>;
	}) => {
		const ref = useRef<HTMLDivElement>(null);
		const [isDragging, setIsDragging] = useState(false);
		const { classes, cx } = useRightSideCanvasStyles({
			isDragging,
			store,
		});

		const handleResize = useCallback(
			(distantToRightEdge: number) => {
				const width =
					distantToRightEdge -
					(window.innerWidth -
						((ref.current?.getBoundingClientRect().x || 0) +
							(ref.current?.getBoundingClientRect().width || 0)));

				store.setPageSideCanvasWidth(width);
			},
			[store]
		);
		const handleStopDragging = useCallback(() => {
			setIsDragging(false);
		}, []);
		const handleStartDragging = useCallback(() => {
			setIsDragging(true);
		}, []);

		return (
			<Group
				ref={ref}
				className={cx(classes.wrapper, classNames?.wrapper)}
				spacing={0}
				p={0}
				noWrap
			>
				<Group spacing={0} w="100%" grow>
					{children}
				</Group>
				<HorizontalResizeHandle
					side="left"
					handleSize={space[3]}
					minWidth={200}
					maxWidth={1600}
					onResize={handleResize}
					onStartDragging={handleStartDragging}
					onStopDragging={handleStopDragging}
					builtinResizeDivider
				/>
			</Group>
		);
	}
);
